<template>
  <div class="static-integration-card" data-testid="integration-card">
    <div class="flex-grow flex flex-col">
      <div>
        <h3 class="static-integration-name" data-testid="integration-name">{{ integration.name }}</h3>
        <p class="static-integration-description" data-testid="integration-description">{{ integration.description }}</p>
        <div v-if="integration.serverUrl" class="static-integration-server" data-testid="integration-server-url">
          <span class="static-integration-server-label">MCP URL</span>
          <div class="static-integration-server-value">
            <code>{{ integration.serverUrl }}</code>
            <button
              type="button"
              class="static-integration-copy"
              :aria-label="copied ? 'Copied' : 'Copy MCP URL'"
              :title="copied ? 'Copied!' : 'Copy MCP URL'"
              data-testid="copy-mcp-url"
              @click="copy(integration.serverUrl)"
            >
              <Icon :name="copied ? 'general.tick' : 'general.copy'" size="sm" />
            </button>
          </div>
        </div>
      </div>
      <div class="static-integration-button">
        <template v-if="integration.disabled">
          <Button
            type="solid"
            variant="blue"
            size="regular"
            @click="$emit('requestIntegration', integration)"
            :data-testid="`request-integration-${integrationSlug}`"
            text="Request to enable"
          />
          <p class="text-xs text-zinc-500 dark:text-zinc-400 mt-1" data-testid="restriction-notice">
            Available on {{ isNewPlanFor2025 ? 'Scale' : 'Enterprise' }} plan.
          </p>
        </template>
        <template v-else>
          <a
            :href="integration.url"
            target="_blank"
            rel="noopener noreferrer"
            class="static-setup-button"
            @click="$emit('trackClick', integration, $event)"
            :data-testid="`setup-button-${integrationSlug}`"
            >Setup</a
          >
          <p class="text-xs mt-1 invisible">&nbsp;</p>
        </template>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useClipboard } from '@vueuse/core';
import { Button, Icon } from '@/storybook';
import type { StaticIntegration } from '../../store/useIntegrationStore';
import { useOrganizationStore } from '@/store/useOrganizationStore';

const props = defineProps<{
  integration: StaticIntegration;
}>();

defineEmits<{
  trackClick: [integration: StaticIntegration, event: MouseEvent];
  requestIntegration: [integration: StaticIntegration];
}>();

const { isNewPlanFor2025 } = storeToRefs(useOrganizationStore());
const { copy, copied } = useClipboard();
const integrationSlug = computed(() => props.integration.name.toLowerCase().replace(/\s+/g, '-'));
</script>

<style scoped>
.static-integration-card {
  @apply flex items-stretch gap-6 p-8 bg-white rounded-xl border border-slate-300 transition-all duration-200 ease-in-out;
  @apply dark:bg-zinc-800 dark:border-zinc-600;
}

.static-integration-card:hover {
  @apply shadow-md;
}

.static-integration-name {
  @apply text-xl font-semibold text-slate-900 mb-2;
  @apply dark:text-zinc-200;
}

.static-integration-description {
  @apply text-sm text-slate-600 leading-6 mb-4;
  @apply dark:text-zinc-400;
}

.static-integration-server {
  @apply flex flex-col gap-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700;
  @apply dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300;
}

.static-integration-server-label {
  @apply font-semibold uppercase tracking-wide text-slate-500;
  @apply dark:text-zinc-500;
}

.static-integration-server-value {
  @apply flex items-center gap-2;
}

.static-integration-server code {
  @apply flex-1 min-w-0 break-all font-mono text-slate-900;
  @apply dark:text-zinc-100;
}

.static-integration-copy {
  @apply flex-shrink-0 inline-flex items-center justify-center rounded-md p-1 text-slate-500 cursor-pointer transition-colors duration-200;
  @apply hover:text-slate-800 hover:bg-slate-200;
  @apply dark:text-zinc-400 dark:hover:text-zinc-100 dark:hover:bg-zinc-700;
}

.static-integration-button {
  @apply mt-auto pt-4;
}

.static-setup-button {
  @apply inline-block px-4 py-2 bg-slate-800 text-white no-underline rounded-md text-sm font-medium transition-colors duration-200;
  @apply hover:bg-slate-700;
  @apply dark:bg-zinc-600 dark:hover:bg-zinc-500;
}
</style>
